import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import QRCode from 'qrcode';
import { AdminServiceTokenControllerService } from '../../api/services/AdminServiceTokenControllerService';
import type { CreatedTokenResponse } from '../../api/models/CreatedTokenResponse';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';

export type ResourceOption = { uuid: string; name: string };

/**
 * NewTokenDialog is rendered as the FormComponent of the CrudPage embedded in
 * MobileTokensDialog (same pattern as OperationDetailDialog →
 * InputQuantitySubDialog). It implements the {@link CrudFormProps} contract
 * plus an extra `userUuid` prop forwarded via CrudPage's `formProps` slot.
 *
 * CrudPage drives lifecycle: when the user clicks Cancel/X we call onClose,
 * when the QR code view's close button is clicked we call onSave so the parent
 * grid refreshes. The actual server call (create-token) happens during the
 * 'form'-step submit; the 'created'-step is purely the QR display, no further
 * backend work.
 */
type Props = {
  userUuid: string;
  /** Pre-loaded by MobileTokensDialog — passing them in avoids a Syncfusion
   *  MultiSelect remount race when the list arrives after mount. */
  availableResources: ResourceOption[];
  // CrudFormProps shape — `value` is unused (no edit flow), `onSave` triggers
  // CrudPage's reload + close, `onClose` closes the dialog without reload.
  value?: unknown;
  onClose: () => void;
  onSave: (value: unknown) => Promise<unknown | void> | unknown | void;
};

type Step = 'form' | 'created';
type Mode = 'operator' | 'shiftLeader';

function buildSetupUrl(rawToken: string): string {
  return `${window.location.origin}/m/setup?bt=${encodeURIComponent(rawToken)}`;
}

function openPrintWindow(qrDataUrl: string, label: string, url: string) {
  const win = window.open('', '_blank', 'width=620,height=820');
  if (!win) {
    return;
  }
  win.document.write(`<!doctype html>
<html>
<head>
  <title>QR-Code · ${label}</title>
  <style>
    body { font-family: sans-serif; margin: 40px; text-align: center; }
    h1 { font-size: 24px; margin-bottom: 8px; }
    .url { font-size: 11px; color: #666; word-break: break-all; margin-top: 20px; }
    img { max-width: 480px; width: 100%; }
    @media print { body { margin: 0; } }
  </style>
</head>
<body>
  <h1>${label}</h1>
  <img src="${qrDataUrl}" alt="QR" />
  <div class="url">${url}</div>
  <script>setTimeout(function(){ window.print(); }, 200);</script>
</body>
</html>`);
  win.document.close();
}

export default function NewTokenDialog({ userUuid, availableResources, onClose, onSave }: Props) {
  const { t } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();

  const [step, setStep] = useState<Step>('form');
  const [mode, setMode] = useState<Mode>('operator');
  const [label, setLabel] = useState('');
  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const [created, setCreated] = useState<CreatedTokenResponse | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  // In operator mode the user must pick exactly one resource. Drop extra
  // selections so the constraint is enforced by the UI rather than only the
  // server.
  const effectiveSelection = useMemo(() => {
    if (mode === 'operator') {
      return selectedUuids.slice(0, 1);
    }
    return selectedUuids;
  }, [mode, selectedUuids]);

  const canSubmit = label.trim().length > 0 && effectiveSelection.length > 0 && !submitting;

  async function handleCreate() {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await AdminServiceTokenControllerService.createToken({
        userUuid,
        resourceUuids: effectiveSelection,
        label: label.trim(),
      });
      setCreated(response);
      if (response.rawToken) {
        const url = buildSetupUrl(response.rawToken);
        const dataUrl = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 2, width: 480 });
        setQrDataUrl(dataUrl);
      }
      setStep('created');
      showToast('success', t('common.success'), t('mobileTokens.new.created'));
    } catch (e) {
      handleError(e, 'creating service token');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDoneClose() {
    // Calling onSave triggers CrudPage's reload + dialog close. The token has
    // already been persisted in the form-step submit; this is purely a
    // lifecycle handoff.
    await onSave({});
  }

  const setupUrl = created?.rawToken ? buildSetupUrl(created.rawToken) : '';

  // Buttons inside Syncfusion DialogComponent's footerTemplate do not receive
  // React onClick events reliably (the wrapper swallows them). Following the
  // pattern from BatchCreation: bind the click handler directly to the rendered
  // DOM element via the ButtonComponent ref.
  const formFooterTemplate = () => (
    <div className="flex justify-end gap-2 p-4 border-t">
      <ButtonComponent
        disabled={submitting}
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => onClose();
          }
        }}
      >
        {t('actions.cancel')}
      </ButtonComponent>
      <ButtonComponent
        cssClass="e-primary"
        disabled={!canSubmit}
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => {
              if (canSubmit) void handleCreate();
            };
          }
        }}
      >
        {submitting ? t('actions.saving') : t('mobileTokens.new.submit')}
      </ButtonComponent>
    </div>
  );

  const createdFooterTemplate = () => (
    <div className="flex justify-end gap-2 p-4 border-t">
      <ButtonComponent
        disabled={!qrDataUrl}
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => {
              if (qrDataUrl && created) {
                openPrintWindow(qrDataUrl, created.label ?? '', setupUrl);
              }
            };
          }
        }}
      >
        {t('mobileTokens.new.print')}
      </ButtonComponent>
      <ButtonComponent
        cssClass="e-primary"
        ref={(btn: any) => {
          if (btn && btn.element) {
            btn.element.onclick = () => void handleDoneClose();
          }
        }}
      >
        {t('actions.close')}
      </ButtonComponent>
    </div>
  );

  // useCallback so the content reference is stable as long as nothing relevant
  // changed. Syncfusion's DialogComponent sometimes remounts its content when
  // the prop reference flips, which would reset state inside (e.g. an
  // already-loaded resources list).
  const formContent = useCallback(() => (
    <div className="p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('mobileTokens.new.label')} <span className="text-red-500">*</span>
        </label>
        <TextBoxComponent
          value={label}
          placeholder={t('mobileTokens.new.labelPlaceholder')}
          change={(e: { value?: string | null }) => setLabel(e.value ?? '')}
        />
      </div>

      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">
          {t('mobileTokens.new.mode')}
        </span>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              checked={mode === 'operator'}
              onChange={() => setMode('operator')}
            />
            <span className="text-sm">{t('mobileTokens.new.modeOperator')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="mode"
              checked={mode === 'shiftLeader'}
              onChange={() => setMode('shiftLeader')}
            />
            <span className="text-sm">{t('mobileTokens.new.modeShiftLeader')}</span>
          </label>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {mode === 'operator'
            ? t('mobileTokens.new.resourceSingle')
            : t('mobileTokens.new.resourceMulti')}
        </label>
        <MultiSelectComponent
          dataSource={availableResources}
          fields={{ text: 'name', value: 'uuid' }}
          value={effectiveSelection}
          placeholder={t('mobileTokens.new.resourcePlaceholder')}
          mode="Box"
          allowFiltering
          change={(e: { value?: string[] | null }) => setSelectedUuids(e.value ?? [])}
        />
        {mode === 'operator' && selectedUuids.length > 1 && (
          <p className="text-xs text-orange-600 mt-1">
            {t('mobileTokens.new.operatorTrim')}
          </p>
        )}
      </div>
    </div>
  ), [t, label, mode, availableResources, effectiveSelection, selectedUuids]);

  const createdContent = useCallback(() => (
    <div className="p-4 space-y-4">
      <p className="text-sm text-gray-600">
        {t('mobileTokens.new.instructions')}
      </p>

      {qrDataUrl ? (
        <div className="flex flex-col items-center">
          <img src={qrDataUrl} alt="QR-Code" className="max-w-xs w-full border rounded" />
          <div className="text-xs text-gray-500 mt-2 break-all max-w-xs text-center">
            {setupUrl}
          </div>
        </div>
      ) : (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      <p className="text-xs text-orange-600">{t('mobileTokens.new.oneTimeWarning')}</p>
    </div>
  ), [t, qrDataUrl, setupUrl]);

  return (
    <DialogComponent
      visible={true}
      isModal={true}
      header={step === 'form' ? t('mobileTokens.new.title') : t('mobileTokens.new.createdTitle')}
      showCloseIcon={true}
      closeOnEscape={true}
      width="560px"
      close={step === 'form' ? onClose : () => void handleDoneClose()}
      target="body"
      animationSettings={{ effect: 'None' }}
      cssClass="new-token-sub-dialog"
      zIndex={10010}
      footerTemplate={step === 'form' ? formFooterTemplate : createdFooterTemplate}
    >
      {step === 'form' ? formContent() : createdContent()}
    </DialogComponent>
  );
}
